// src/components/Admin/TaskManager/tabs/JobExecutionsTab.tsx

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Button,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import { useJobExecutions, useJobDetails } from '../../../hooks/useJobExecutions';
import JobDetailsDialog from './JobDetailsDialog';
import { JobExecution, EXECUTION_STATUS_OPTIONS } from '../../../types/taskManager';
import { format } from 'date-fns';
import JsonView from "@uiw/react-json-view";
import JsonViewEditor from "@uiw/react-json-view/editor";

const JobExecutionsTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());

  const {
    jobs,
    loading,
    refetch,
    retryJob,
    cancelStuck,
    isRetrying,
    isCancelling,
  } = useJobExecutions({ status: statusFilter || undefined });

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const openJobDetails = (jobId: string) => {
    setSelectedJobId(jobId);
    setDetailsOpen(true);
  };

  const handleRetry = async (jobId: string) => {
    if (window.confirm('Retry this job? A new job will be created and submitted to the queue.')) {
      try {
        const result = await retryJob(jobId);
        alert(`Job resubmitted! New Job ID: ${result.new_job_id || 'N/A'}`);
      } catch (error: any) {
        alert(`Failed to retry: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleBulkRetry = async () => {
    if (window.confirm(`Retry ${selectedJobs.size} selected job(s)?`)) {
      let succeeded = 0;
      let failed = 0;

      for (const jobId of Array.from(selectedJobs)) {
        try {
          await retryJob(jobId);
          succeeded++;
        } catch (error) {
          failed++;
          console.error(`Failed to retry ${jobId}:`, error);
        }
      }

      alert(`Retry complete:\n✓ ${succeeded} succeeded\n✗ ${failed} failed`);
      setSelectedJobs(new Set());
    }
  };

  const handleCancelStuck = async () => {
    if (window.confirm('Cancel all jobs pending/running for more than 1 hour?')) {
      try {
        const result = await cancelStuck({ hours: 1 });
        alert(`Cancelled ${result.jobs_cancelled || 0} stuck job(s)`);
      } catch (error: any) {
        alert(`Failed: ${error.message || 'Unknown error'}`);
      }
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedJobs(new Set(jobs.map(j => j.job_id)));
    } else {
      setSelectedJobs(new Set());
    }
  };

  const handleSelectJob = (jobId: string, checked: boolean) => {
    const newSelected = new Set(selectedJobs);
    if (checked) {
      newSelected.add(jobId);
    } else {
      newSelected.delete(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const getStatusColor = (status: string) => {
    const option = EXECUTION_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || '#9e9e9e';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const calculateDuration = (start?: string, end?: string) => {
    if (!start || !end) return 'N/A';
    try {
      const duration = (new Date(end).getTime() - new Date(start).getTime()) / 1000;
      if (duration < 60) return `${duration.toFixed(1)}s`;
      if (duration < 3600) return `${(duration / 60).toFixed(1)}m`;
      return `${(duration / 3600).toFixed(1)}h`;
    } catch {
      return 'N/A';
    }
  };

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={() => setStatusFilter('')}
            variant={statusFilter === '' ? 'contained' : 'outlined'}
          >
            All
          </Button>
          {EXECUTION_STATUS_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              size="small"
              onClick={() => setStatusFilter(opt.value)}
              variant={statusFilter === opt.value ? 'contained' : 'outlined'}
            >
              {opt.label}
            </Button>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            color="warning"
            startIcon={<CancelIcon />}
            onClick={handleCancelStuck}
            disabled={isCancelling}
            variant="outlined"
          >
            Cancel Stuck
          </Button>

          <IconButton onClick={() => refetch()} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Bulk Actions Bar */}
      {selectedJobs.size > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="primary.contrastText">
              {selectedJobs.size} job(s) selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<ReplayIcon />}
                onClick={handleBulkRetry}
                disabled={isRetrying}
              >
                Retry Selected
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedJobs(new Set())}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Jobs Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Alert severity="info">No job executions found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedJobs.size > 0 && selectedJobs.size < jobs.length}
                    checked={jobs.length > 0 && selectedJobs.size === jobs.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell width={40}></TableCell>
                <TableCell>Job ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="center">Tasks</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <React.Fragment key={job.id}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedJobs.has(job.job_id)}
                        onChange={(e) => handleSelectJob(job.job_id, e.target.checked)}
                      />
                    </TableCell>

                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(job.id)}>
                        {expandedRows.has(job.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {job.job_id.length > 40 ? `${job.job_id.substring(0, 40)}...` : job.job_id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip label={job.job_type} size="small" variant="outlined" />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={job.status}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(job.status),
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(job.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption">
                        {calculateDuration(job.started_at, job.completed_at)}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={job.tasks?.length || 0}
                        size="small"
                        color={job.tasks?.some(t => t.status === 'failed') ? 'error' : 'default'}
                      />
                    </TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Details">
                          <IconButton size="small" onClick={() => openJobDetails(job.job_id)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {(job.status === 'failed') && (
                          <Tooltip title="Retry">
                            <IconButton
                              size="small"
                              onClick={() => handleRetry(job.job_id)}
                              color="primary"
                              disabled={isRetrying}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, bgcolor: '#fafafa' }}>
                      <Collapse in={expandedRows.has(job.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          {job.error_message && (
                            <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem' }}>
                              <strong>Job Error:</strong> {job.error_message}
                            </Alert>
                          )}

                          {job.tasks && job.tasks.length > 0 && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Tasks ({job.tasks.length})
                              </Typography>

                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Task ID</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Order</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Result</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {job.tasks.map((task) => (
                                    <TableRow key={task.id}>
                                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                        {task.task_id}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>
                                        {task.task_type}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>
                                        {task.task_order}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={task.status}
                                          size="small"
                                          sx={{
                                            fontSize: '0.65rem',
                                            height: 20,
                                            bgcolor: getStatusColor(task.status),
                                            color: 'white',
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem' }}>
                                        {calculateDuration(task.started_at, task.completed_at)}
                                      </TableCell>
                                      <TableCell>
                                        {task.result && (
                                          <Box sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Typography variant="caption">
                                              {task.result.status && `Status: ${task.result.status}`}
                                              {task.result.notifications_sent !== undefined &&
                                                ` | Sent: ${task.result.notifications_sent}`}
                                            </Typography>
                                          </Box>
                                        )}
                                        {task.error_message && (
                                          <Tooltip title={task.error_message}>
                                            <Chip label="Error" size="small" color="error" sx={{ fontSize: '0.65rem', height: 18 }} />
                                          </Tooltip>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </>
                          )}

                          {job.result && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" display="block" gutterBottom>
                                <strong>Job Result:</strong>
                              </Typography>
                              <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <JsonView
                                  value={job.result}
                                  collapsed={2}
                                  displayDataTypes={false}
                                  displayObjectSize={false}
                                  enableClipboard
                                />
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Job Details Dialog */}
      <JobDetailsDialog
        open={detailsOpen}
        jobId={selectedJobId}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedJobId(null);
        }}
        onRetry={handleRetry}
      />
    </Box>
  );
};

export default JobExecutionsTab;